export type TimedEventDay = {
	date: string;
} & Record<string, string>;

export interface ITimedEvent {
	label: string;
	timeFormatted: string;
	time24h: string;
}

export const formatDailyTimedEvents = (
	data: TimedEventDay[],
	lang: "ar" | "en",
	type: "tide" | "prayer",
	dateParams = new Date(),
	sortByDate: boolean,
): ITimedEvent[] => {
	const todayDay = dateParams.getDate();

	const todayItem = data.find((item) => {
		const itemDay = new Date(item.date).getDate();
		return itemDay === todayDay;
	});

	if (!todayItem) {
		return [];
	}

	const { date, ...events } = todayItem;

	const items = Object.entries(events).map(([label, time24h]) => ({
		label: type === "tide" ? formatTideKeyToLabel(label) : label,
		time24h,
		timeFormatted: formatTime(time24h, lang),
		date,
	}));

	if (sortByDate) {
		return items
			.sort((a, b) => toMinutes(a.time24h) - toMinutes(b.time24h))
			.map((item) => item);
	}

	return items.map((item) => item);
};

export const formatTideKeyToLabel = (key: string): string => {
	const parts = key.split("_");

	const index = Number(parts.pop());
	const label = parts.join(" ");

	return `${formatOrdinal(index)} ${capitalizeWords(label)}`;
};

const formatOrdinal = (num: number): string => {
	if (num >= 11 && num <= 13) {
		return `${num}th`;
	}

	switch (num % 10) {
		case 1:
			return `${num}st`;
		case 2:
			return `${num}nd`;
		case 3:
			return `${num}rd`;
		default:
			return `${num}th`;
	}
};

const capitalizeWords = (text: string): string =>
	text
		.split(" ")
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(" ");

const toMinutes = (time24h: string): number => {
	const [hours, minutes] = time24h.split(":").map(Number);
	return hours * 60 + minutes;
};

const formatTime = (time24h?: string, lang: "ar" | "en" = "en"): string => {
	if (!time24h) return "";

	const cleaned = time24h.replace(/[^\d:]/g, "");
	const [hoursStr, minutesStr] = cleaned.split(":");

	const hours = Number(hoursStr);
	const minutes = Number(minutesStr);

	if (isNaN(hours) || isNaN(minutes)) return "";

	const formattedHours = hours % 12 === 0 ? 12 : hours % 12;

	const period =
		lang === "ar" ? (hours >= 12 ? "م" : "ص") : hours >= 12 ? "PM" : "AM";

	return `${formattedHours}:${minutes.toString().padStart(2, "0")} ${period}`;
};
